// src/admin/adminErrorCopy.js — how the superadmin console words a failed call (v206).
//
// ⛔ Never err.message: adminApi.js throws buildApiError, whose message is the server's English text. The sentence
// comes from err.code / err.status. An unmapped code prints the code (a support handle, CLAUDE.md); a 5xx carries
// the request reference (purge refusals are flat {error_code, message} — no reference, and none is needed).
// Keys live in admin.json `actionErrors`; call with the `admin` namespace's t.

const CODED = new Set([
  'TENANT_NOT_RETIRED',
  'TENANT_PROTECTED',
  'PURGE_ACTIVE_WORK',
  'CANNOT_PURGE_SELF',
  'CANNOT_PURGE_SUPERADMIN',
  'CANNOT_PURGE_SYSTEM_ACTOR',
  'CANNOT_REMOVE_LAST_CUSTOMER_ADMIN',
  'PURGE_CONFIRMATION_MISMATCH',
  'PURGE_IN_PROGRESS',
  'USER_PURGED',
  'CUSTOMER_NOT_DEACTIVATED',
  'PURGE_MODE_MISMATCH',
  'ERASE_REFUSED_LIVE_DATA',
  'STRIPE_UNAVAILABLE',
]);

// "No such target": already purged elsewhere, or deleted meanwhile. The caller re-reads its list.
const GONE = new Set(['TENANT_NOT_FOUND', 'USER_NOT_FOUND', 'RESOURCE_NOT_FOUND', 'CUSTOMER_NOT_FOUND']);

export function isGoneError(err) {
  return GONE.has(err?.code);
}

/** `{ key, params }` for `err`. `purge: true` = the 5xx sentence says re-running is safe (purges are idempotent). */
export function adminErrorCopy(err, { purge = false } = {}) {
  const code = typeof err?.code === 'string' ? err.code : '';
  const status = typeof err?.status === 'number' ? err.status : null;
  if (CODED.has(code)) return { key: `actionErrors.${code}`, params: {} };
  if (GONE.has(code)) return { key: 'actionErrors.notFound', params: {} };
  // An old backend has no purge routes (FastAPI's generic 404, not one of our codes) or does not know a filter
  // (422 UNKNOWN_QUERY_PARAM, e.g. `without_workspaces` before BE 289980e).
  if (status === 404 || code === 'UNKNOWN_QUERY_PARAM') return { key: 'actionErrors.unavailable', params: {} };
  if (status !== null && status >= 500) {
    return { key: purge ? 'actionErrors.purgeServer' : 'actionErrors.server', params: { ref: err?.requestId || '—' } };
  }
  return { key: 'actionErrors.unknown', params: { code: code || (status !== null ? String(status) : '—') } };
}

/** The sentence itself, with the `admin` namespace's `t`. */
export function adminErrorText(t, err, options) {
  const { key, params } = adminErrorCopy(err, options);
  return t(key, params);
}
